<script setup lang="ts">
import { MeasureResult } from '@Shared/utils/types';
import Gauge from './parts/Gauge.vue';
import MetricBreakdown from './parts/MetricBreakdown.vue';
import { calculateDeltaValue, DeltaType, MetricType } from './utility';

const props = defineProps<{
  metricValue: MeasureResult;
  title: string;
  metricType: MetricType;
  inPopup: boolean;
  count: number;
  metricBreakdown: {
    negative: MeasureResult;
    neutral: MeasureResult;
    positive: MeasureResult;
  };
}>();

const emit = defineEmits<{
  (event: 'showDetails'): void;
}>();

const delta = computed(() => calculateDeltaValue(props.metricValue, DeltaType.PointDelta));
</script>
<template>
  <div>
    <div class="flex flex-row items-center justify-between mt-2">
      <div
        class="mr-4 font-bold xl:text-4 2xl:text-5 3xl:text-10 xs:text-5 3xl:mt-8 text-[#4F4F4F] font-family[Poppins]"
      >
        {{ title }}
      </div>
      <div
        v-if="!inPopup"
        @click="emit('showDetails')"
        class="xs:hidden lg:flex text-3 mr-4 font-[Roboto] text-[#C73362] underline cursor-pointer"
        id="show-primary-metric-modal-button"
      >
        View More
      </div>
      <a
        v-if="!inPopup"
        @click="emit('showDetails')"
        id="show-primary-metric-modal-button-mobile"
        class="lg:hidden inline ml-auto i-mdi-chevron-right bg-[#4F4F4F] w-8 h-8 cursor-pointer"
      ></a>
    </div>
    <div
      class="mt-6 mb-6 flex items-center justify-between"
      :class="{
        'sm:flex-row xs:flex-col 2xl:mx-4': !inPopup,
        'flex-col': inPopup,
      }"
    >
      <Gauge
        class="h-[110px] xs:max-h-[130px] xl:max-h-auto"
        :delta="delta"
        :value="metricValue.currentValue"
        :metricType="metricType"
      ></Gauge>
      <div
        :class="{
          'w-full items-start pt-4': inPopup,
          'items-center xl:h-[130px] xs:h-[180px] md:mr-4 2xl:mr-8': !inPopup,
        }"
        class="xl:text-4 flex flex-col xs:pt-5 xl:pt-0 xs:text-5 xl:text-left text-[#4F4F4F]"
      >
        <span class="min-w-full xs:px-4">
          <b>{{ count }}</b> Responses
        </span>
        <div
          :class="{
            'w-full': inPopup,
          }"
        >
          <MetricBreakdown
            :class="{
              'lg:pt-3 xs:px-4': inPopup,
              'pt-2': !inPopup,
            }"
            :metric-type="metricType"
            :metric-breakdown="metricBreakdown"
            :in-popup="inPopup"
          ></MetricBreakdown>
        </div>
      </div>
    </div>
  </div>
</template>
